import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import Select from "react-select";
import {
  Card,
  CardBody,
  Col,
  Container,
  Row,
  Label,
  FormFeedback,
  Input,
  Form
} from "reactstrap";
import toast from "react-hot-toast";
import {
  getAllEvents,
  getEventVenuesBySlug,
  getAllTicketByEventAndVenue,
  getAllTicketByEvent,
  createFreeBooking,
  sendGroupEmailConfirmation,
} from "api";
import { handleError } from "utils/errorHandler";

const FreeBooking = () => {
  document.title = "Free Booking | EventoX";

  const [eventList, setEventList] = useState([]);
  const [venueList, setVenueList] = useState([]);
  const [ticketTypes, setTicketTypes] = useState([]);
  const [venueLoading, setVenueLoading] = useState(false);
  const [ticketLoading, setTicketLoading] = useState(false);


  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      firstName: "",
      lastName: "",
      phoneNumber: "",
      emailId: "",
      eventSlug: "",
      venueId: "",
      ticketTypeId: "",
      maxCount: 1,
    },
    validationSchema: Yup.object({
      firstName: Yup.string().required("First name is required"),
      lastName: Yup.string().required("Last name is required"),
      phoneNumber: Yup.string().required("Phone number is required"),
      emailId: Yup.string().email("Invalid email").required("Email is required"),
      maxCount: Yup.number().min(1).required("Max count required"),
      eventSlug: Yup.string().required("Event is required"),
      ticketTypeId: Yup.string().required("Ticket is required"),
    }),
    onSubmit: async (values, { resetForm }) => {
      try {
        const selectedEvent = eventList.find(e => e.slugId === values.eventSlug);
        if (!selectedEvent) return toast.error("Event not found");

        const bookingPayload = {
          primaryContactDetails: {
            firstName: values.firstName,
            lastName: values.lastName,
            phoneNo: values.phoneNumber,
            email: values.emailId,
            address: "",
            country: "",
            state: "",
            city: "",
            zipCode: "",
          },
          attendees: Array.from({ length: values.maxCount - 1 }).map((_, index) => ({
            name: `Attendee ${index + 1} (${values.firstName} ${values.lastName})`,
            email: values.emailId,
            phoneNo: values.phoneNumber,
            address: "",
            country: "",
            state: "",
            city: "",
            zipCode: "",
            ticketTypeId: values.ticketTypeId,
          })),
          totalTicketCount: values.maxCount,
          totalAmount: 0,
          grandTotalAmount: 0,
          processingFee: 0,
          ticketFee: 0,
          discountAmount: 0,
          eventData: {
            eventId: selectedEvent?.eventId,
            eventName: selectedEvent?.eventName,
            description: selectedEvent?.description || "",
            Country: { currency: selectedEvent?.Country?.currency || "usd" },
            Organizer: { organizerId: selectedEvent?.Organizer?.organizerId || 1 },
            showOtherFees: false,
          },
          venueId: values.venueId,
          ticketList: [{ ticketTypeId: values.ticketTypeId }],
          bookingSlotId: null,
        };

        console.log("PAYLOAD", bookingPayload);
        const resp = await createFreeBooking(bookingPayload);
        if(resp&&resp.status===200){
          toast.success("Free booking created");
          await sendGroupEmailConfirmation(resp.bookingNo);
        }
        console.log("RESP", resp);
        

        setVenueList([]);
        setTicketTypes([]);
        resetForm();
      } catch (err) {
        console.error("❌ Error during booking:", err);
        handleError(err);
      }
    },
  });

  useEffect(() => {
    (async () => {
      try {
        const events = await getAllEvents();
        setEventList(events);
      } catch (err) {
        handleError(err);
      }
    })();
  }, []);

  const fetchAllTicket = async (slugId, venueId) => {
    try {
      setTicketLoading(true);
      if (!slugId) return;
      const resp = venueId
        ? await getAllTicketByEventAndVenue(slugId, venueId)
        : await getAllTicketByEvent(slugId);

      setTicketTypes(resp.map(t => ({ value: t.ticketTypeId, label: t.ticketType })));
    } catch (error) {
      handleError(error);
    } finally {
      setTicketLoading(false);
    }
  };


  return (
    <div className="page-content">
      <Container fluid>
        <Row>
          <Col lg="12">
            <Card>
              <CardBody>
                <h4 className="mb-4">Create Free Booking</h4>
                <Form
                  onSubmit={e => {
                    e.preventDefault();
                    validation.handleSubmit();
                    return false;
                  }}
                >
                  <Row>
                    <Col md={6}>
                      {[
                        ["First Name", "firstName"],
                        ["Last Name", "lastName"],
                        ["Phone Number", "phoneNumber"],
                        ["Email ID", "emailId"],
                        ["Max Count", "maxCount", "number"],
                      ].map(([label, name, type = "text"]) => (
                        <div className="mb-3" key={name}>
                          <Label>{label}</Label>
                          <Input
                            name={name}
                            type={type}
                            placeholder={label}
                            value={validation.values[name]}
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            invalid={validation.touched[name] && !!validation.errors[name]}
                          />
                          {validation.touched[name] && validation.errors[name] && (
                            <FormFeedback type="invalid">{validation.errors[name]}</FormFeedback>
                          )}
                        </div>
                      ))}
                    </Col>

                    <Col md={6}>
                      {/* Event Select */}
                      <div className="mb-3">
                        <Label>Select Event</Label>
                        <Select
                          value={
                            eventList
                              .map(e => ({ value: e.slugId, label: e.eventName }))
                              .find(e => e.value === validation.values.eventSlug) || null
                          }
                          options={eventList.map(e => ({
                            value: e.slugId,
                            label: e.eventName,
                          }))}
                          onChange={async (data) => {
                            const slug = data?.value || "";
                            validation.setFieldValue("eventSlug", slug);

                            setVenueList([]);
                            setTicketTypes([]);
                            setVenueLoading(true);
                            setTicketLoading(true);

                            const venues = await getEventVenuesBySlug(slug);
                            const mappedVenues = venues.map(v => ({
                              value: v.venueId,
                              label: v.venueName,
                            }));
                            setVenueList(mappedVenues);
                            setVenueLoading(false);

                            if (mappedVenues.length === 1) {
                              validation.setFieldValue("venueId", mappedVenues[0].value);
                              await fetchAllTicket(slug, mappedVenues[0].value);
                            } else {
                              validation.setFieldValue("venueId", "");
                              await fetchAllTicket(slug, null);
                            }
                            setTicketLoading(false);
                          }}
                          styles={{ menu: provided => ({ ...provided, zIndex: 9999 }) }}
                        />
                        {validation.touched.eventSlug && validation.errors.eventSlug && (
                          <div className="text-danger mt-1">{validation.errors.eventSlug}</div>
                        )}
                      </div>

                      {/* Venue Select */}
                      {venueList.length > 1 && (
                        <div className="mb-3">
                          <Label>Select Venue</Label>
                          <Select
                            value={
                              venueList.find(v => v.value === validation.values.venueId) || null
                            }
                            isLoading={venueLoading}
                            isDisabled={venueLoading}
                            options={venueList}
                            onChange={async (data) => {
                              const venueId = data?.value || "";
                              validation.setFieldValue("venueId", venueId);
                              validation.setFieldValue("ticketTypeId", "");
                              await fetchAllTicket(validation.values.eventSlug, venueId);
                            }}
                            styles={{ menu: provided => ({ ...provided, zIndex: 9999 }) }}
                          />
                        </div>
                      )}

                      {/* Ticket Select */}
                      {validation.values.eventSlug &&
                        (venueList.length <= 1 || validation.values.venueId) && (
                          <div className="mb-3">
                            <Label>Select Ticket</Label>
                            <Select
                              value={
                                ticketTypes.find(t => t.value === validation.values.ticketTypeId) || null
                              }
                            isLoading={ticketLoading}
                            isDisabled={ticketLoading || !ticketTypes.length}
                              options={ticketTypes}
                              onChange={(data) =>
                                validation.setFieldValue("ticketTypeId", data?.value || "")
                              }
                              styles={{ menu: provided => ({ ...provided, zIndex: 9999 }) }}
                            />
                            {validation.touched.ticketTypeId && validation.errors.ticketTypeId && (
                              <div className="text-danger mt-1">
                                {validation.errors.ticketTypeId}
                              </div>
                            )}
                          </div>
                        )}
                    </Col>
                  </Row>

                  <Row>
                    <Col className="text-end">
                      <button type="submit" className="btn btn-success">
                        Create Booking
                      </button>
                    </Col>
                  </Row>
                </Form>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default FreeBooking;
